import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { ParcelCard } from 'components/ParcelCard';
import { TripCard } from 'components/TripCard';
import { EmptyState, ListSkeleton } from 'components/EmptyState';
import api from 'lib/api';
import { toast } from 'sonner';
import { Search, Package, Plane, BellPlus } from 'lucide-react';
import { useLanguage } from 'lib/i18n';

export default function SearchPage() {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'trip');
  const [from, setFrom] = useState(params.get('departure_city') || '');
  const [to, setTo] = useState(params.get('arrival_city') || '');
  const [via, setVia] = useState(params.get('via_city') || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);

  const saveSearch = async () => {
    try {
      await api.post('/saved-searches', {
        kind: tab === 'parcel' ? 'deliveries' : 'trips',
        departure_city: from || undefined,
        arrival_city: to || undefined,
      });
      toast.success(t('savedSearch.alertSaved'));
    } catch {
      toast.error(t('savedSearch.alertSaveFailed'));
    }
  };

  const search = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (from) q.set('departure_city', from);
    if (to) q.set('arrival_city', to);
    if (via && tab === 'trip') q.set('via_city', via);
    const urlParams = new URLSearchParams(q);
    urlParams.set('tab', tab);
    setParams(urlParams);
    try {
      const r = await api.get(`/${tab === 'parcel' ? 'deliveries' : 'trips'}?${q.toString()}`);
      setItems(r.data);
    } catch {}
    setLoading(false);
  }, [from, to, tab, via, setParams]);

  useEffect(() => { searchRef.current = search; }, [search]);
  useEffect(() => { searchRef.current?.(); }, [tab]);

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('search.title')}</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="trip" data-testid="search-tab-trip"><Plane size={14} className="mr-1"/> {t('search.trips')}</TabsTrigger>
          <TabsTrigger value="parcel" data-testid="search-tab-parcel"><Package size={14} className="mr-1"/> {t('search.parcels')}</TabsTrigger>
        </TabsList>
        <Card className="mb-4">
          <CardContent className="p-4 space-y-3">
            <div className="grid sm:grid-cols-3 gap-2">
            <Input placeholder={t('search.filter.departure')} value={from} onChange={e => setFrom(e.target.value)} data-testid="search-from-input" />
            <Input placeholder={t('search.filter.arrival')} value={to} onChange={e => setTo(e.target.value)} data-testid="search-to-input" />
            {tab === 'trip' && <Input placeholder={t('search.filter.via')} value={via} onChange={e => setVia(e.target.value)} data-testid="search-via-input" />}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={search} data-testid="search-button" className="flex-1"><Search size={14} className="mr-1"/> {t('search.submit')}</Button>
            <Button variant="outline" onClick={saveSearch} type="button" className="flex-1"><BellPlus size={14} className="mr-1"/> {t('savedSearch.createAlert')}</Button>
            </div>
          </CardContent>
        </Card>
        <TabsContent value="trip">
          {loading ? <ListSkeleton /> : items.length === 0 ? (
            <EmptyState icon={Plane} title={t('search.empty.noTrips')} description={t('search.empty.tryOtherCities')} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(trip => <TripCard key={trip.id} trip={trip} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="parcel">
          {loading ? <ListSkeleton /> : items.length === 0 ? (
            <EmptyState icon={Package} title={t('search.empty.noParcels')} description={t('search.empty.tryOtherCities')} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(p => <ParcelCard key={p.id} parcel={p} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
